import { z } from "zod";
import { FIELD_UPDATE_SOURCE } from "@common/constants";

const MONETARY_MAGNITUDE_LIMIT = 100_000_000_000;
const QUANTITY_MAGNITUDE_LIMIT = 999_999;

export const accrualMonetaryAmountSchema = z
    .number()
    .finite()
    .min(-MONETARY_MAGNITUDE_LIMIT)
    .max(MONETARY_MAGNITUDE_LIMIT);

export const accrualNonnegativeMonetaryAmountSchema = z
    .number()
    .finite()
    .min(0)
    .max(MONETARY_MAGNITUDE_LIMIT);

export const accrualQuantitySchema = z
    .number()
    .finite()
    .min(-QUANTITY_MAGNITUDE_LIMIT)
    .max(QUANTITY_MAGNITUDE_LIMIT)
    .nullable()
    .optional();
export const accrualUnitPriceSchema = accrualMonetaryAmountSchema
    .nullable()
    .optional();

export const AccrualExtractionLineItemSchema = z.object({
    item: z.string().max(500).nullable().optional(),
    description: z.string().max(2000).nullable(),
    amount: accrualMonetaryAmountSchema,
    quantity: accrualQuantitySchema,
    unitPrice: accrualUnitPriceSchema,
    classIds: z.array(z.string().uuid()).max(50).nullable(),
    accountId: z.string().max(255).nullable(),
    accrualAccountId: z.string().max(255).nullable(),
    accountIdSource: z.nativeEnum(FIELD_UPDATE_SOURCE).nullable().optional(),
    tax: accrualNonnegativeMonetaryAmountSchema.nullable().optional(),
});

export const ACCRUAL_PARTY_MAX_NAMES = 50;
export const ACCRUAL_PARTY_MAX_NAME_LENGTH = 500;
export const ACCRUAL_PARTY_MAX_EMAILS = 50;
export const ACCRUAL_PARTY_MAX_EMAIL_LENGTH = 500;
export const ACCRUAL_PARTY_MAX_ADDRESSES = 20;
export const ACCRUAL_PARTY_MAX_ADDRESS_LENGTH = 2000;
export const ACCRUAL_PARTY_MAX_PHONE_NUMBERS = 20;
export const ACCRUAL_PARTY_MAX_PHONE_LENGTH = 100;

export const AccrualPartyIdentifiersSchema = z.object({
    names: z
        .array(z.string().max(ACCRUAL_PARTY_MAX_NAME_LENGTH))
        .max(ACCRUAL_PARTY_MAX_NAMES)
        .default([]),
    emails: z
        .array(z.string().max(ACCRUAL_PARTY_MAX_EMAIL_LENGTH))
        .max(ACCRUAL_PARTY_MAX_EMAILS)
        .default([]),
    addresses: z
        .array(z.string().max(ACCRUAL_PARTY_MAX_ADDRESS_LENGTH))
        .max(ACCRUAL_PARTY_MAX_ADDRESSES)
        .default([]),
    phoneNumbers: z
        .array(z.string().max(ACCRUAL_PARTY_MAX_PHONE_LENGTH))
        .max(ACCRUAL_PARTY_MAX_PHONE_NUMBERS)
        .default([]),
});

export const AccrualDocumentSchema = z.object({
    counterpartyName: z.string().max(500).nullable(),
    documentNumber: z.string().max(500).nullable(),
    documentDate: z.string().date().nullable(),
    dueDate: z.string().date().nullable(),
    totalAmount: accrualMonetaryAmountSchema.nullable(),
    taxAmount: accrualNonnegativeMonetaryAmountSchema.nullable().optional(),
    discountAmount: accrualNonnegativeMonetaryAmountSchema
        .nullable()
        .optional(),
    depositAmount: accrualNonnegativeMonetaryAmountSchema.nullable().optional(),
    memo: z.string().max(5000).nullable().optional(),
    balanceDue: accrualMonetaryAmountSchema.nullable().optional(),
    paymentDate: z.string().date().nullable().optional(),
    entityId: z.number().int().nullable(),
    issuer: AccrualPartyIdentifiersSchema.nullable().optional(),
    recipient: AccrualPartyIdentifiersSchema.nullable().optional(),
    isInvoiceOrBill: z.boolean().nullable().optional(),
    warnings: z.array(z.string().max(500)).max(50),
    lineItems: z.array(AccrualExtractionLineItemSchema).max(500),
});

export type AccrualExtractionLineItem = z.infer<
    typeof AccrualExtractionLineItemSchema
>;
export type AccrualPartyIdentifiers = z.infer<
    typeof AccrualPartyIdentifiersSchema
>;
export type AccrualDocument = z.infer<typeof AccrualDocumentSchema>;

export const AccrualDocumentExtractionSchema = z.object({
    documents: z.array(AccrualDocumentSchema).min(1).max(50),
});

export type AccrualDocumentExtraction = z.infer<
    typeof AccrualDocumentExtractionSchema
>;
